import { FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DisciplinaItem from './DisciplinaItem';
import Button from 'Components/UI/Button';
import SearchInput from 'Components/UI/SearchInput';
import { Icon, IconName } from 'Components/UI/Icon';
import { IDiscipline, IDisciplineData } from 'Data/interfaces/Obra/IObraData';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { disciplineIcons, resolveCustomDisciplineIcon } from 'Utils/Icons';
import { useQueryDisciplineLibraries } from 'Hooks/disciplines/useQueryDisciplineLibraries';
import DisciplineModal, { IDisciplineFormValues } from 'Pages/EditObra/components/Disciplines/components/DisciplineModal';
import Library, { ISelectedItem } from 'Pages/EditObra/components/Disciplines/components/Library';
import { Container, Toolbar } from './styles';

const resolveDisciplineIcon = (discipline: IDisciplineData): IconName => (
  discipline.IsCustom
    ? resolveCustomDisciplineIcon(discipline.Icon)
    : disciplineIcons.get(discipline.Icon) || 'menuContexto'
);

interface IDiscipliansTab {
  isSubmited?: boolean;
  onSubmit: (props?: object, error?: string[]) => void;
}

const DiscipliansTab: FC<Props> = ({
  isSubmited,
  disciplinesData,
  theme,
  onSubmit,
}) => {
  const { t } = useTranslation('editObra');

  const [disciplinesList, setDisciplinesList] = useState<IDisciplineData[]>([]);
  const [showDisciplineModal, setShowDisciplineModal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [search, setSearch] = useState('');

  const {
    data: libraries,
    isFetching: listLibrariesLoading,
    refetch: refetchLibraries,
  } = useQueryDisciplineLibraries();

  const onCheck = (index: number) => {
    disciplinesList[index].IsChecked = !disciplinesList[index].IsChecked;
    setDisciplinesList(JSON.parse(JSON.stringify(disciplinesList)));
  };

  const disciplinesForValidation = useMemo((): IDiscipline[] => (
    disciplinesList
      .filter((discipline) => discipline.IsChecked)
      .map((discipline) => ({
        DisciplineFk: discipline.DisciplineId,
        IsCustom: discipline.IsCustom,
        CustomName: discipline.CustomDispName,
        CustomAcronym: discipline.CustomAcronym,
        Discipline: {
          DisciplineId: discipline.DisciplineId,
          IsChecked: discipline.IsChecked,
          IsCustom: discipline.IsCustom,
          Name: discipline.Name,
          CustomDispName: discipline.CustomDispName,
          Icon: discipline.Icon,
        },
      }))
  ), [disciplinesList]);

  const filteredDisciplines = useMemo(() => {
    const withIndex = disciplinesList.map((discipline, index) => ({ discipline, index }));

    if (!search) return withIndex;

    const lowerSearch = search.toLowerCase();
    return withIndex.filter(({ discipline }) => (
      (discipline.CustomDispName ?? discipline.Name)?.toLowerCase().includes(lowerSearch) ||
      discipline.CustomAcronym?.toLowerCase().includes(lowerSearch)
    ));
  }, [disciplinesList, search]);

  const openLibrary = () => {
    refetchLibraries();
    setShowLibrary(true);
  };

  const handleDisciplineSubmit = (values: IDisciplineFormValues) => {
    const newDiscipline: IDisciplineData = {
      DisciplineId: 9,
      Name: 'Personalizado',
      CustomDispName: values.name,
      CustomAcronym: values.acronym,
      IsChecked: true,
      IsCustom: true,
      Icon: values.icon,
    };
    setDisciplinesList((prev) => [...prev, newDiscipline]);
    setShowDisciplineModal(false);
  };

  const handleImportItems = (items: ISelectedItem[]) => {
    const newDisciplines: IDisciplineData[] = items.map((item) => ({
      DisciplineId: 9,
      Name: 'Personalizado',
      CustomDispName: item.name,
      CustomAcronym: item.acronym,
      IsChecked: true,
      IsCustom: true,
      Icon: item.icon,
    }));
    setDisciplinesList((prev) => [...prev, ...newDisciplines]);
    setShowLibrary(false);
  };

  const hasErrors = () => {
    const withoutDisciplines = disciplinesList.find((discipline) => discipline.IsChecked) === undefined;
    if (withoutDisciplines) {
      return ['Pelo menos uma disciplina deve ser selecionada'];
    }
  };

  useEffect(() => {
    const list: IDisciplineData[] = JSON.parse(JSON.stringify(disciplinesData));
    setDisciplinesList([...list]);
  }, [disciplinesData]);

  if (isSubmited) {
    const errors = hasErrors();
    if (errors !== undefined) {
      onSubmit(undefined, errors);
    } else {
      // Shape must match the backend's BindConstructionSiteDisciplineViewModel
      // (Icon/Acronym, not CustomIcon/CustomAcronym) — this is the wire payload
      // for POST /Obras/Create, not an IDiscipline.
      const Disciplines = disciplinesList
        .filter((discipline) => discipline.IsChecked)
        .map((discipline) => ({
          CustomName: discipline.CustomDispName,
          DisciplineFk: discipline.DisciplineId,
          IsCustom: discipline.IsCustom,
          Icon: discipline.Icon,
          Acronym: discipline.CustomAcronym,
        }));

      onSubmit({ Disciplines: [...Disciplines] });
    }
  }

  return (
    <Container>
      <div>
        <span className="titleTab">
          Selecione as disciplinas desejadas
        </span>

        <Toolbar>
          <div className="searchBox">
            <SearchInput
              setSearch={setSearch}
              inputPlaceholder={t('disciplines.searchPlaceholder')}
            />
          </div>
          <Button type="text" className="toolbarBtn" onClick={openLibrary}>
            <Icon icon="listDetailVision" customSize={16} className="actionIcon" />
            <span className="textBtn">{t('disciplines.libraryButton')}</span>
          </Button>
          <Button type="text" className="toolbarBtn" onClick={() => setShowDisciplineModal(true)}>
            <Icon icon="adicionar" customSize={14} className="actionIcon" />
            <span className="textBtn">{t('disciplines.createButton')}</span>
          </Button>
        </Toolbar>

        <div className="disciplinasList">
          {filteredDisciplines.map(({ discipline, index }) => (
            <DisciplinaItem
              key={index}
              disciplina={discipline.CustomDispName ?? discipline.Name}
              acronym={discipline.CustomAcronym}
              icon={resolveDisciplineIcon(discipline)}
              checked={discipline.IsChecked}
              disabled={discipline.Disabled}
              onCheck={() => onCheck(index)}
              theme={theme}
            />
          ))}
        </div>
      </div>

      {showDisciplineModal && (
        <DisciplineModal
          visible={showDisciplineModal}
          editDiscipline={null}
          disciplines={disciplinesForValidation}
          onSubmit={handleDisciplineSubmit}
          onCancel={() => setShowDisciplineModal(false)}
        />
      )}

      {showLibrary && (
        <Library
          visible={showLibrary}
          libraries={libraries}
          disciplines={disciplinesForValidation}
          listLoading={listLibrariesLoading}
          onImport={handleImportItems}
          onCancel={() => setShowLibrary(false)}
        />
      )}
    </Container>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  disciplinesData: state.obra.Disciplines,
  theme: state.multiDomain.theme,
});

const connector = connect(
  mapState,
);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IDiscipliansTab;

export default connector(DiscipliansTab);
